// `formatDateTime` lived here and did `new Date(dateStr).toLocaleDateString(...)`.
// It had ZERO consumers, and it was a trap: API timestamps are naive Eastern, so
// that would have silently reinterpreted them as the viewer's local zone. Real
// -world timestamps now go through `formatSiteTime` in lib/userTime.ts, which
// converts to the viewer's chosen zone and labels it. Sim-calendar dates stay
// here on `fmtSimDate` below, which is deliberately timezone-stable.

/**
 * Format a date as M/D/YYYY, read in a TIMEZONE-STABLE way so the rendered day
 * never shifts between viewers. Use for sim (in-game calendar) dates — which
 * are universal — and for the date PART of any value shown verbatim (no local
 * timezone conversion). The single canonical M/D/YYYY formatter; ~10 local
 * copies were collapsed into this.
 *
 * Strings are parsed by REGEX (never `new Date()`), so a date-only or
 * UTC-midnight value can't drift a day:
 *   - ISO       "2026-06-01" / "2026-06-01T00:00:00Z"  -> "6/1/2026"
 *   - legacy US "5/27/1997 12:00:00 AM" / "5/27/1997"  -> "5/27/1997"
 * Date objects are read via their UTC components. Returns '' for
 * nullish / empty / unparseable input.
 *
 * NOT for real-world timestamps you want in the viewer's LOCAL time (logins,
 * post times, when a game was actually played) — those keep a local formatter.
 */
export function fmtSimDate(d: Date | string | null | undefined): string {
  if (d == null) return ''
  if (typeof d !== 'string') {
    return Number.isNaN(d.getTime())
      ? ''
      : `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`
  }
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${Number(iso[2])}/${Number(iso[3])}/${iso[1]}`
  const us = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (us) return `${Number(us[1])}/${Number(us[2])}/${us[3]}`
  return ''
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Like {@link fmtSimDate} but "Month D, YYYY" — same timezone-stable regex
 * parsing, no `Intl`/`Date` in the path so the day can't drift for a viewer
 * outside Eastern. For prose-y contexts that want the long form of a SIM date
 * (e.g. the game page's document title, which legacy rendered via
 * `playedOnLongFormat`).
 */
export function fmtSimDateLong(d: Date | string | null | undefined): string {
  const short = fmtSimDate(d)
  if (!short) return ''
  const [m, day, year] = short.split('/')
  return `${MONTH_NAMES[Number(m) - 1]} ${Number(day)}, ${year}`
}

/**
 * Like {@link fmtSimDate} but M/D with the year dropped — same timezone-stable
 * regex parsing. For dense single-context tables (e.g. a one-season game log)
 * where the year is already implied and repeating it is noise.
 */
export function fmtSimDateShort(d: Date | string | null | undefined): string {
  if (d == null) return ''
  if (typeof d !== 'string') {
    return Number.isNaN(d.getTime()) ? '' : `${d.getUTCMonth() + 1}/${d.getUTCDate()}`
  }
  const iso = d.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${Number(iso[2])}/${Number(iso[3])}`
  const us = d.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (us) return `${Number(us[1])}/${Number(us[2])}`
  return ''
}
